/**
 * Il controllo che fa **scattare** i promemoria.
 *
 * ## Il pezzo che mancava
 *
 * Fino al 03-10-2026 i promemoria erano segnalibri: dodici file li mettevano, toglievano e
 * disegnavano, e nessuno guardava l'orologio. Questo è il file che guarda l'orologio.
 *
 * ## Un riscontro a schermo, non una schermata
 *
 * L'avviso passa dal sistema dei riscontri che è già montato sopra tutte le schermate. Una finestra
 * dedicata avrebbe dovuto decidere cosa fare del telecomando mentre è aperta — rubare il fuoco a chi
 * sta guardando un film è peggio che non avvisare — e sopravvivere al cambio di schermata. Il
 * riscontro fa già entrambe le cose.
 *
 * ## La regola non è qui
 *
 * Quando avvisare, per quanto, e quale scegliere fra due: sta in `reminderAlerts` nel dominio, dove
 * si prova senza aspettare che passi un minuto vero. Qui c'è solo il mestiere di guardare l'ora.
 */

import { useEffect, useRef } from 'react';
import { reminderToAnnounce, type Program } from '@player/domain';
import { useToast, useTranslator } from '@player/ui';

/**
 * Ogni quanto si guarda l'ora.
 *
 * Venti secondi: la finestra d'annuncio è di un minuto prima più cinque dopo, quindi non se ne può
 * perdere uno. Più spesso sarebbe lavoro per niente su un apparecchio da trenta euro; molto meno
 * spesso rischierebbe di annunciare un programma già cominciato da un pezzo.
 */
const TICK_MS = 20_000;

export function useReminderAlerts(
  programs: ReadonlyArray<Program>,
  reminders: ReadonlyArray<string>,
  channelNameOf: (channelId: string) => string,
  nowMs: () => number,
): void {
  const toast = useToast();
  const translator = useTranslator();

  /*
    Quelli già annunciati, **per tutta la sessione**.

    In un riferimento e non in uno stato: cambiarlo non deve ridisegnare niente, e soprattutto non
    deve rientrare fra le dipendenze dell'effetto — rifarebbe partire il timer a ogni annuncio.
  */
  const annunciati = useRef<Set<string>>(new Set());

  /*
    Tutto il resto in riferimenti, per lo stesso motivo.

    Programmi e promemoria cambiano di continuo — ne arriva uno a ogni canale che si apre. Se
    fossero dipendenze, il timer ripartirebbe da zero a ogni cambiamento e l'avviso non scatterebbe
    mai: è il difetto che si vede solo aspettando, cioè mai durante lo sviluppo.
  */
  const dati = useRef({ programs, reminders, channelNameOf, nowMs, toast, translator });
  dati.current = { programs, reminders, channelNameOf, nowMs, toast, translator };

  useEffect(() => {
    const guarda = (): void => {
      const d = dati.current;
      const voce = reminderToAnnounce(
        new Set(d.reminders),
        d.programs,
        d.nowMs(),
        annunciati.current,
      );
      if (voce === null) return;

      annunciati.current.add(voce.key);
      const channel = d.channelNameOf(voce.channelId);
      /*
        Senza il programma si dice il **canale**, e si avvisa comunque.

        L'ora sta nella chiave, quindi il momento si sa; il titolo no, perché il palinsesto in
        memoria è una finestra. Pretenderlo avrebbe voluto dire tacere proprio sui promemoria messi
        dalla home — quelli di giorni dopo, gli unici che uno si dimentica davvero.
      */
      d.toast.show(
        voce.program === null
          ? d.translator.t('reminder.startingOnChannel', { channel })
          : d.translator.t('reminder.starting', { title: voce.program.title, channel }),
      );
    };

    // Anche subito: chi accende il televisore un minuto prima di un programma segnato deve essere
    // avvisato adesso, non al prossimo giro.
    guarda();

    const id = setInterval(guarda, TICK_MS);
    return () => clearInterval(id);
  }, []);
}
